import moment from 'moment-timezone';
import map from 'lodash/map';
import { humanize } from '@utils';
import alphanumericSort from '@utils/alphanumericSort';
import dimensionStore from '@globalStore/dimensionsStore';
import { DIMENSION_TYPES_CONSTANTS } from '@utils/metrics';
import { formatTime } from '@components/metrics-panel-hz/utils/format';

const metricTypes = {
  DIMENSIONS: 'dimensions',
  METRICS: 'metrics',
};

export const customerOverrideForDrilldown = {
  1960180521: 'user_email',
  1928809131: 'user_email', //c3.dryrun account on qe2
};

/**
 * [Remove 3 OOB metrics and high cardinality dimensions](https://conviva.atlassian.net/browse/HP-7194)
 * The hardcode dimensions will be removed from dimension list, but we can still use them on users table
 */
export const ECO_RESERVED_DIMENSION_INFOS = {
  app_user_id: {
    field: 'app_user_id',
    name: 'User ID',
    type: 'immutableGlobalMetadata',
    groupBy: 'app_user_id',
    category: 'user',
  },
  user_email: {
    field: 'user_email',
    name: 'User Email',
    type: 'immutableGlobalMetadata',
    groupBy: 'user_email',
    category: 'user',
  },
  client_id: {
    field: 'client_id',
    name: 'Client ID',
    type: 'immutableGlobalMetadata',
    groupBy: 'client_id',
    category: 'content',
  },
};

export const ECO_RESERVED_DIMENSIONS = Object.keys(ECO_RESERVED_DIMENSION_INFOS);

const getHardcodedDimensions = (customerId) => {
  return ['client_id', customerOverrideForDrilldown[customerId] || 'app_user_id'];
};

export const getHardCodeDimensionsInfo = (customerId) => {
  return getHardcodedDimensions(customerId).map((key) => ECO_RESERVED_DIMENSION_INFOS[key]);
};

const getStaticDimensions = (customerId) => {
  return [...getHardcodedDimensions(customerId), 'device_name'];
};

export const getMandatoryDimensions = (customerId: number, selectedMetric: string) => {
  const staticDimensions = getStaticDimensions(customerId);

  return [...staticDimensions, selectedMetric];
};

function replaceObjectKeys(obj, keyMap) {
  const newObj = { ...obj };
  for (const oldKey in keyMap) {
    if (oldKey in newObj) {
      const newKey = keyMap[oldKey];
      newObj[newKey] = newObj[oldKey];
      delete newObj[oldKey];
    }
  }
  return newObj;
}

function reorderObjectKeys(obj, keyOrder) {
  const reorderedObject = {
    Mandatory: [],
  };
  for (const key of keyOrder) {
    if (key in obj) {
      reorderedObject[key] = obj[key];
    }
  }
  return reorderedObject;
}

function modifyArrayOfObjectsForKey(obj, metricType) {
  for (const key in obj) {
    obj[key] = obj[key].map((item) => {
      return { ...item, metric: item.key, type: metricType };
    });
  }
  return obj;
}

export const getDimensionData = ({
  dimensionsCategories,
  currentDimensionsCategoryOrder,
  dimensionsListCategory,
}) => {
  const reorderedCategories = reorderObjectKeys(
    dimensionsCategories,
    currentDimensionsCategoryOrder,
  );
  const replacedCategories = replaceObjectKeys(reorderedCategories, dimensionsListCategory);
  const dimensionData = modifyArrayOfObjectsForKey(replacedCategories, metricTypes.DIMENSIONS);
  return dimensionData;
};

export const getMetricsData = (metricsCategories, categoryMeta) => {
  const validMetrics = {};
  for (const key in metricsCategories) {
    const categoryData = categoryMeta[key] || { displayName: key };
    validMetrics[categoryData.displayName] = [];
    const metrics = Object.values(metricsCategories[key]);
    metrics.forEach((metric: any) => {
      if (
        !['undefined', 'in_progress'].includes(metric.definitionStatus) &&
        metric.active &&
        !metric.relatedAvgMetric
      ) {
        const metricDetail = {
          key: metric.name,
          metric: metric.name,
          name: metric.displayName,
          type: metricTypes.METRICS,
        };
        validMetrics[categoryData.displayName].push(metricDetail);
      }
    });
    if (validMetrics[categoryData.displayName].length === 0) {
      delete validMetrics[categoryData.displayName];
    }
  }
  return validMetrics;
};

export const getFlowMetricsData = (flowMetrics, flowCategoryMeta) => {
  const flowMetricsData = {};
  for (const key in flowMetrics) {
    const metrics = flowMetrics[key] || [];
    const flowName = flowCategoryMeta[key]?.displayName || key;
    flowMetricsData[flowName] = [];
    metrics.forEach((metric) => {
      if (metric.active) {
        const metricDetail = {
          key: metric.id,
          metric: metric.id,
          name: metric.displayName,
          type: metricTypes.METRICS,
        };
        flowMetricsData[flowName].push(metricDetail);
      }
    });
  }
  return flowMetricsData;
};

export const getExtractedMandatory = (categoriesData, mandatoryDimensions) => {
  const mandatoryCategories: any = [];
  for (const key in categoriesData) {
    categoriesData[key] = categoriesData[key].filter((item) => {
      if (!mandatoryDimensions.includes(item.key)) {
        return item;
      } else {
        mandatoryCategories.push(item);
      }
    });
  }
  categoriesData['Mandatory'] = mandatoryCategories;
  return categoriesData;
};

export const getOrderedMetrics = (metricList, dimensionList, customerId) => {
  const staticDimensions = getStaticDimensions(customerId);
  const dimensionKeys = map(dimensionList, 'field');
  const metricKeys = Object.keys(metricList);
  const orderedMetrics: any = [...staticDimensions, ...dimensionKeys, ...metricKeys];
  return orderedMetrics;
};

export const getSegregatedMetrics = (selectedMetrics, metricList) => {
  const segregatedMetrics: any = {
    dimensions: [],
    metrics: [],
    otherMetrics: [],
  };
  for (const metric of selectedMetrics) {
    if (metricList[metric]) {
      if (metricList[metric].type === metricTypes.DIMENSIONS) {
        segregatedMetrics.dimensions.push(metric);
      } else {
        segregatedMetrics.metrics.push(metric);
      }
    } else {
      segregatedMetrics.otherMetrics.push(metric);
    }
  }
  return segregatedMetrics;
};

const formatStyle = 'HHH DD, HH:MM';

export const humanizeHoc = (value, unit) => {
  if (value === null || value === undefined || value === 'NA') return value;
  switch (unit) {
    case 'Kbps':
      return humanize(value * 1000, 'bps') + 'bps';
    case 'min':
    case 'mins':
      return formatTime(value * 60 * 1000);
    case 'hour':
      return formatTime(value * 60 * 60 * 1000);
    case 'sec':
      return formatTime(value * 1000);
    case 'ms':
      return formatTime(value);
    case 'fps':
    case '%':
      return `${Number(value.toFixed(2))}${unit}`;
    case 'unitless':
    case 'ratio':
      if (isNaN(value)) {
        return value;
      } else {
        return `${Number(Number(value).toFixed(2))}`;
      }

    default:
      return `${humanize(value, unit)}`;
  }
};

export const momentTime = (value) => moment(value).format(formatStyle);

export const getSortAndSearchTable = (data, searchData, sortData, selectedMetrics) => {
  const filledData = data.map((item) => {
    const newItem = { ...item };
    for (const metric of selectedMetrics) {
      if (newItem[metric] === undefined) {
        newItem[metric] = 'NA';
      }
    }
    return newItem;
  });
  let finalData = filledData;
  const { text, key: searchKey } = searchData;
  const { order, key: sortKey } = sortData;
  const re = new RegExp(text, 'i');
  if (searchKey && selectedMetrics.includes(searchKey)) {
    const keyFixed = searchKey.replace(/\$/g, '.');
    finalData = finalData.filter((item) => item[keyFixed] && re.test(item[keyFixed]));
  }
  if (sortKey && selectedMetrics.includes(sortKey)) {
    const result: any = {
      remainingObjects: [],
      removedObjects: [],
    };
    finalData.forEach((obj) => {
      if (obj[sortKey] === 'NA') {
        result.removedObjects.push(obj);
      } else {
        result.remainingObjects.push(obj);
      }
    });
    const sortedData = result.remainingObjects.sort((prev, next) =>
      alphanumericSort(order, prev[sortKey], next[sortKey]) ? 1 : -1,
    );
    finalData = [...sortedData, ...result.removedObjects];
  }
  return finalData;
};

export const filteredDimensions = (dimensions, filterMode) => {
  let returnDimensions = dimensions;
  const { dimensionByTypes } = dimensionStore.getState();
  if (
    filterMode === DIMENSION_TYPES_CONSTANTS.immutableGlobalMetadata ||
    filterMode === DIMENSION_TYPES_CONSTANTS.mutableGlobalMetadata
  ) {
    returnDimensions = dimensions.filter(
      (dimension) =>
        dimensionByTypes[dimension] !== DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata,
    );
  }
  return returnDimensions;
};

export const MAX_ANCHORED_COUNT = 3;
